import { useNavigate } from "@tanstack/react-router";
import { Col, Input, Row } from "antd";
import { useResetPasswordMutation } from "api/User/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AuthFormWrapper } from "components/Wrappers/AuthFormWrapper/AuthFormWrapper.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";

import { ChangePasswordFormData, schema } from "./formSchema";

interface ChangePasswordFormContentProps {
    isSubmitting?: boolean;
}

const ChangePasswordFormContent: FC<ChangePasswordFormContentProps> = ({ isSubmitting }) => {
    const { t } = usePaldockTranslation("forms.change-password");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { control, fieldNames } = useGeneralForm<ChangePasswordFormData>();

    return (
        <AuthFormWrapper
            buttonText={t("actions.change_password")}
            hideLogo
            loading={isSubmitting}
            buttonDisabled={isSubmitting}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.password.name}
                        label={t("fields.new_password.label")}
                        className="!mb-0"
                    >
                        <Input.Password placeholder={tFields("password.placeholder")} />
                    </FormItem>
                </Col>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.password_confirmation.name}
                        label={tFields("password_confirmation.label")}
                        className="!mb-0"
                    >
                        <Input.Password placeholder={tFields("password_confirmation.placeholder")} />
                    </FormItem>
                </Col>
            </Row>
        </AuthFormWrapper>
    );
};

interface ChangePasswordFormProps {
    tenantSlug?: string;
}

export const ChangePasswordForm: FC<ChangePasswordFormProps> = ({ tenantSlug }) => {
    const { t } = usePaldockTranslation("forms.change-password");
    const navigate = useNavigate();

    const url = new URL(window.location.href);
    const email = url.searchParams.get("email") || "";
    const token = url.searchParams.get("token") || "";

    const navigateToLogin = () => {
        if (tenantSlug) {
            navigate({ to: "/$tenantSlug/auth/login", params: { tenantSlug } });
        } else {
            navigate({ to: "/auth/login" });
        }
    };

    const resetPasswordMutation = useResetPasswordMutation({
        onSuccess: () => {
            toast.success(t("messages.update_success"));
            navigateToLogin();
        },
        onError: (errorResponse) => {
            toast.error(`${t("messages.update_error")}: ${errorResponse.message}`);
        },
    });

    const onSubmit = (data: ChangePasswordFormData) => {
        resetPasswordMutation.mutate({
            token,
            email,
            password: data.password,
            password_confirmation: data.password_confirmation,
        });
    };

    if (!email || !token) {
        toast.error(t("messages.missing_parameters"));
        navigateToLogin();
        return null;
    }

    return (
        <GeneralFormProvider<ChangePasswordFormData>
            schema={schema}
            defaultValues={{ password: "", password_confirmation: "" }}
            onSubmit={onSubmit}
            requiredMark={false}
        >
            <ChangePasswordFormContent isSubmitting={resetPasswordMutation.isPending} />
        </GeneralFormProvider>
    );
};
